/**
 * Autocomplete configuration for product filters
 * Based on the OpenAPI spec endpoints
 */

export interface FilterAutocompleteOption {
  label: string;
  value: string | number;
  group?: string;
  productCode?: string | number; // Add productCode for productName searches
}

export interface FilterAutocompleteConfig {
  endpoint?: string;
  transformResponse?: (data: any) => FilterAutocompleteOption[];
}

/**
 * Configuration mapping filter field names to API endpoints and data transformations
 */
export const filterAutocompleteConfig: Record<
  string,
  FilterAutocompleteConfig
> = {
  productCode: {
    endpoint: "/Odata/V1/ProductAdministrationOverview",
    transformResponse: (data: any[]) =>
      data
        .map((item) => ({
          label: `${item.productCode || item.ProductCode || ""} - ${item.productName || item.ProductName || ""}`,
          value: item.productCode || item.ProductCode,
        }))
        .filter((item) => item.value), // Filter out items without productCode
  },

  productName: {
    endpoint: "/Odata/V1/ProductAdministrationOverview",
    transformResponse: (data: any[]) =>
      data
        .map((item) => ({
          label: item.productName || item.ProductName || "",
          value: item.productName || item.ProductName,
          productCode: item.productCode || item.ProductCode, // Include productCode for filtering
        }))
        .filter((item) => item.value), // Filter out items without productName
  },

  productGroupCode: {
    endpoint: "/api/v1/ProductGroups",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: `${item.code} - ${item.name}`,
        value: item.code,
        group: "Product Groups",
      })),
  },

  productGroupName: {
    endpoint: "/api/v1/ProductGroups",
    transformResponse: (data: any[]) =>
      data
        .map((item) => ({
          label: item.name,
          value: item.name,
          group: "Product Groups",
        }))
        .filter((item) => item.value)
        .sort((a, b) => a.label.localeCompare(b.label)),
  },

  gewascode: {
    endpoint: "/api/v1/Genera",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: `${item.code} - ${item.name}`,
        value: item.code,
        group: "Crops",
      })),
  },

  gewasnummer: {
    endpoint: "/api/v1/Crops",
    transformResponse: (data: any[]) =>
      data
        .map((item) => ({
          label: String(item.taxonomicNumber),
          value: item.taxonomicNumber,
          group: "Crop Numbers",
        }))
        .sort((a, b) => Number(a.value) - Number(b.value)),
  },

  coolingRegimeTemperature: {
    endpoint: "/api/v1/CoolingRegimeTemperatures",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: item.description,
        value: item.coolingRegimeTemperatureId,
        group: "Cooling Temperatures",
      })),
  },

  coolingRegimePriority: {
    endpoint: "/api/v1/CoolingRegimePriorities",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: item.description,
        value: item.coolingRegimePriorityId,
        group: "Cooling Priorities",
      })),
  },

  characteristic: {
    endpoint: "/api/v1/Characteristics",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: `${item.code} - ${item.name}`,
        value: item.code,
        group: "Characteristics",
      })),
  },

  registration: {
    endpoint: "/api/v1/CoolingRegimeGenus",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: `CRG-${item.coolingRegimeGenusId ?? 0}`,
        value: item.coolingRegimeGenusId ?? 0,
        group: "Registrations",
      })),
  },

  registrationCode: {
    endpoint: "/api/v1/ProductGroups",
    transformResponse: (data: any[]) =>
      data
        .map((item) => ({
          label: `${item.code} - ${item.name}`,
          value: item.code,
        }))
        .filter((item) => item.value), // Filter out items without product group code
  },

  genusName: {
    endpoint: "/Odata/V1/GenusOverview",
    transformResponse: (data: any[]) =>
      data
        .map((item) => ({
          label: item.name || item.Name || "",
          value: item.name || item.Name,
        }))
        .filter((item) => item.value && item.label), // Filter out items without name
  },

  temperatureId: {
    endpoint: "/api/v1/CoolingRegimeTemperatures",
    transformResponse: (data: any[]) => {
      const temperatureOptions = data.map((item) => ({
        label: `T${item.coolingRegimeTemperatureId} (Temperatuur)`,
        value: item.coolingRegimeTemperatureId,
        group: "Temperaturen",
      }));
      return temperatureOptions;
    },
  },

  description: {
    endpoint:
      "/api/v1/CoolingRegimeTemperatures,/api/v1/CoolingRegimePriorities",
    transformResponse: (data: any[]) => {
      const seen = new Set<string>();
      const options: FilterAutocompleteOption[] = [];

      data.forEach((item) => {
        if (item.description && !seen.has(item.description)) {
          seen.add(item.description);
          const group =
            item.coolingRegimeTemperatureId !== undefined
              ? "Temperaturen"
              : "Prioriteiten";
          options.push({
            label: item.description,
            value: item.description,
            group,
          });
        }
      });

      return options.sort((a, b) => {
        if (a.group !== b.group) {
          return (a.group || "").localeCompare(b.group || "");
        }
        return a.label.localeCompare(b.label);
      });
    },
  },

  modifiedBy: {
    endpoint:
      "/api/v1/CoolingRegimeTemperatures,/api/v1/CoolingRegimePriorities",
    transformResponse: (data: any[]) => {
      const seen = new Set<string>();
      const options: FilterAutocompleteOption[] = [];

      data.forEach((item) => {
        if (item.lastModifiedBy && !seen.has(item.lastModifiedBy)) {
          seen.add(item.lastModifiedBy);
          const group =
            item.coolingRegimeTemperatureId !== undefined
              ? "Temperaturen"
              : "Prioriteiten";
          options.push({
            label: item.lastModifiedBy,
            value: item.lastModifiedBy,
            group,
          });
        }
      });

      return options;
    },
  },

  // Temperature-specific filters
  temperatureDescription: {
    endpoint: "/api/v1/CoolingRegimeTemperatures",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: item.description,
        value: item.description,
        group: "Temperaturen",
      })),
  },

  temperatureModifiedBy: {
    endpoint: "/api/v1/CoolingRegimeTemperatures",
    transformResponse: (data: any[]) => {
      const seen = new Set<string>();
      const options: FilterAutocompleteOption[] = [];

      data.forEach((item) => {
        if (item.lastModifiedBy && !seen.has(item.lastModifiedBy)) {
          seen.add(item.lastModifiedBy);
          options.push({
            label: item.lastModifiedBy,
            value: item.lastModifiedBy,
            group: "Temperaturen",
          });
        }
      });

      return options;
    },
  },

  // Priority-specific filters
  priorityId: {
    endpoint: "/api/v1/CoolingRegimePriorities",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: `P${item.coolingRegimePriorityId} (Prioriteit)`,
        value: item.coolingRegimePriorityId,
        group: "Prioriteiten",
      })),
  },

  priorityDescription: {
    endpoint: "/api/v1/CoolingRegimePriorities",
    transformResponse: (data: any[]) =>
      data.map((item) => ({
        label: item.description,
        value: item.description,
        group: "Prioriteiten",
      })),
  },

  priorityModifiedBy: {
    endpoint: "/api/v1/CoolingRegimePriorities",
    transformResponse: (data: any[]) => {
      const seen = new Set<string>();
      const options: FilterAutocompleteOption[] = [];

      data.forEach((item) => {
        if (item.lastModifiedBy && !seen.has(item.lastModifiedBy)) {
          seen.add(item.lastModifiedBy);
          options.push({
            label: item.lastModifiedBy,
            value: item.lastModifiedBy,
            group: "Prioriteiten",
          });
        }
      });

      return options;
    },
  },
};

/**
 * Helper function to get autocomplete config for a specific filter
 */
export const getFilterAutocompleteConfig = (
  filterName: string,
): FilterAutocompleteConfig | undefined => {
  return filterAutocompleteConfig[filterName];
};

/**
 * Helper function to check if a filter supports autocomplete
 */
export const hasAutocompleteConfig = (filterName: string): boolean => {
  return filterName in filterAutocompleteConfig;
};
